import { useEffect, useRef, useState } from "react";
import { CameraOff, VideoOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { Tile } from "./Tile";
import { StatusPill } from "./StatusPill";

interface CameraTileProps {
  name: string;
  /** Homey image resource id (UUID) fetched via the /api/homey/image proxy. */
  imageId: string;
  /** Live camera connection status; drives the online/offline pill. */
  online: boolean;
  /** How often to refresh the snapshot, in ms. */
  refreshMs?: number;
  className?: string;
}

/**
 * A camera/doorbell snapshot tile. The image is a periodically refreshed still
 * (live RTSP video is not feasible). The browser never sees the Homey token —
 * the request goes to the server proxy with the Supabase JWT, and the proxy
 * fetches the snapshot from Homey. Polling pauses while the tab is hidden.
 *
 * To test: open the dashboard, Security tab. The doorbell tile shows a live
 * snapshot that refreshes every ~7s; hide the tab and polling stops; an offline
 * doorbell shows the placeholder instead of a broken image.
 */
export function CameraTile({
  name,
  imageId,
  online,
  refreshMs = 7_000,
  className,
}: CameraTileProps) {
  const [token, setToken] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [errored, setErrored] = useState(false);
  const mountedRef = useRef(true);

  // Resolve the current Supabase access token (same source as the WS client).
  useEffect(() => {
    mountedRef.current = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (mountedRef.current) setToken(data.session?.access_token ?? null);
    });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Poll by bumping the nonce; pause while the tab is hidden to avoid hammering
  // the Homey when nobody is looking.
  useEffect(() => {
    if (typeof document === "undefined") return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer !== null) return;
      timer = setInterval(() => setNonce((n) => n + 1), refreshMs);
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else {
        setNonce((n) => n + 1); // refresh immediately on return
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refreshMs]);

  const canShow = token !== null && imageId !== "" && !errored;
  const src = canShow
    ? `/api/homey/image?token=${encodeURIComponent(token)}&imageId=${encodeURIComponent(imageId)}&nonce=${nonce}`
    : null;

  return (
    <Tile domain="security" className={cn("gap-3 p-3", className)}>
      <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-muted">
        {src !== null ? (
          <img
            src={src}
            alt={`${name} snapshot`}
            className="size-full object-cover"
            onError={() => setErrored(true)}
          />
        ) : (
          <div className="flex size-full flex-col items-center justify-center gap-2 text-muted-foreground">
            {online ? (
              <CameraOff className="size-6" aria-hidden />
            ) : (
              <VideoOff className="size-6" aria-hidden />
            )}
            <span className="text-xs">{online ? "No snapshot" : "Offline"}</span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-sm font-medium">{name}</span>
        <StatusPill status={online ? "ok" : "offline"}>{online ? "Online" : "Offline"}</StatusPill>
      </div>
    </Tile>
  );
}
